/**
 * CLIENTE DA API
 *
 * A peça que faltava entre o protótipo e o backend. Sem ela o sistema tem
 * um núcleo sólido e nenhum usuário — que é exatamente onde vocês estão.
 *
 * Resolve quatro coisas que, feitas tela a tela, viram bug espalhado:
 * renovação de token, erro em português, fila offline e envio de foto.
 */

/**
 * Onde a API responde.
 *
 * Em produção, no próprio endereço do site: o netlify.toml repassa /auth,
 * /os, /clientes… para a Railway. Por isso o padrão de produção é caminho
 * relativo — o mesmo pacote funciona em eixoautocontrol.com.br, no endereço
 * .netlify.app antigo e em prévia de deploy, e o cookie de login fica sempre
 * no endereço que a pessoa abriu.
 *
 * Era um endereço fixo (bancada-sul.netlify.app). Com o domínio novo, quem
 * entrasse por ele faria login num site e guardaria o cookie em outro — e a
 * sessão cairia a cada 15 minutos, sem erro nenhum na tela.
 */
export const BASE = import.meta.env?.VITE_API ?? (import.meta.env?.PROD ? "" : "http://localhost:3000");

let tokenAcesso: string | null = null;
let oficinaAtual: string | null = null;
let renovando: Promise<void> | null = null;

export interface Sessao {
  tokenAcesso: string; expiraEm: number;
  usuario: { id: string; nome: string; email: string };
  oficinas: Array<{ id: string; nome: string; papel: string }>;
  oficinaAtual: string | null;
}

export class ErroApi extends Error {
  constructor(readonly codigo: string, mensagem: string, readonly status: number,
              readonly esperarSegundos?: number) { super(mensagem); }
}

/* ── sessão ───────────────────────────────────────────────────────────── */

export function definirSessao(s: Sessao) {
  tokenAcesso = s.tokenAcesso;
  oficinaAtual = s.oficinaAtual;
  // Só o essencial para reidratar a tela: o refresh vive em cookie httpOnly,
  // fora do alcance de qualquer script. Guardar token de acesso aqui é
  // aceitável porque ele vale 15 minutos.
  sessionStorage.setItem("bs.sessao", JSON.stringify({
    usuario: s.usuario, oficinas: s.oficinas, oficinaAtual: s.oficinaAtual,
  }));
}

export function limparSessao() {
  tokenAcesso = null; oficinaAtual = null;
  sessionStorage.removeItem("bs.sessao");
}

export const sessaoSalva = (): Omit<Sessao, "tokenAcesso" | "expiraEm"> | null => {
  const s = sessionStorage.getItem("bs.sessao");
  return s ? JSON.parse(s) : null;
};

/* ── requisição ───────────────────────────────────────────────────────── */

/**
 * Renova o token quando vence.
 *
 * A promessa compartilhada evita a estampida: sem ela, uma tela com seis
 * requisições paralelas dispara seis renovações — e como o refresh rotaciona
 * a cada uso, cinco delas seriam tratadas como reúso e derrubariam a sessão
 * inteira do usuário legítimo.
 */
async function renovar(): Promise<void> {
  if (renovando) return renovando;
  renovando = (async () => {
    const r = await fetch(`${BASE}/auth/renovar`, { method: "POST", credentials: "include" });
    if (!r.ok) { limparSessao(); throw new ErroApi("sessao_expirada", "Sua sessão expirou. Entre de novo.", 401); }
    const d = await r.json();
    tokenAcesso = d.tokenAcesso;
  })().finally(() => { renovando = null; });
  return renovando;
}

export async function api<T = any>(
  caminho: string,
  opcoes: {
    metodo?: string; corpo?: any; idempotencia?: string; tentouRenovar?: boolean;
    /** Bytes que vão como estão — foto, não JSON. */
    binario?: Blob; mime?: string;
  } = {},
): Promise<T> {
  const cabecalhos: Record<string, string> = {
    "Content-Type": opcoes.binario ? (opcoes.mime ?? "image/jpeg") : "application/json",
  };
  if (tokenAcesso) cabecalhos.Authorization = `Bearer ${tokenAcesso}`;
  if (oficinaAtual) cabecalhos["X-Oficina"] = oficinaAtual;
  // Toque duplo no celular com luva é regra, não exceção. A chave faz a
  // segunda chamada devolver o resultado da primeira em vez de duplicar.
  if (opcoes.idempotencia) cabecalhos["Idempotency-Key"] = opcoes.idempotencia;

  const r = await fetch(`${BASE}${caminho}`, {
    method: opcoes.metodo ?? "GET",
    headers: cabecalhos,
    credentials: "include",
    body: opcoes.binario ?? (opcoes.corpo ? JSON.stringify(opcoes.corpo) : undefined),
  });

  /**
   * Um 401 vindo de /auth/* é a RESPOSTA, não um token vencido.
   *
   * Sem essa ressalva, toda falha de login disparava renovar(), que falhava
   * por não haver sessão e lançava "Sua sessão expirou. Entre de novo." —
   * substituindo a mensagem real. O dono digitava a senha errada e lia que a
   * sessão tinha expirado, sem sessão nenhuma para expirar.
   *
   * Pior no segundo fator: `mfa_requerido` também é 401, então o pedido do
   * código virava "sessão expirada" e a tela nunca chegava a perguntar. Com
   * MFA ligado, ninguém entrava. O mesmo valia para `muitas_tentativas`.
   */
  const ehAutenticacao = caminho.startsWith("/auth/");
  if (r.status === 401 && !opcoes.tentouRenovar && !ehAutenticacao) {
    await renovar();
    return api<T>(caminho, { ...opcoes, tentouRenovar: true });
  }

  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new ErroApi(
      d.erro ?? "falha",
      // A API já devolve mensagem em português pensada para o balconista.
      // Se não devolveu, é falha inesperada e a referência liga a reclamação
      // à linha do log.
      d.mensagem ?? `Algo deu errado${d.referencia ? ` (ref. ${d.referencia})` : ""}.`,
      r.status,
      Number(r.headers.get("Retry-After")) || undefined,
    );
  }

  return r.status === 204 ? (undefined as T) : r.json();
}

const get = <T = any>(c: string) => api<T>(c);
const post = <T = any>(c: string, corpo?: any, idem?: string) =>
  api<T>(c, { metodo: "POST", corpo, idempotencia: idem });
const patch = <T = any>(c: string, corpo: any) => api<T>(c, { metodo: "PATCH", corpo });

/* ── autenticação ─────────────────────────────────────────────────────── */

export const auth = {
  async entrar(email: string, senha: string, codigoMfa?: string): Promise<Sessao> {
    const s = await api<Sessao>("/auth/entrar", {
      metodo: "POST", corpo: { email, senha, codigoMfa },
    });
    definirSessao(s);
    return s;
  },
  async sair() { await post("/auth/sair").catch(() => {}); limparSessao(); },
  trocarSenha: (senhaAtual: string, senhaNova: string) =>
    post("/auth/senha", { senhaAtual, senhaNova }),

  /**
   * O servidor aceita cadastro?
   *
   * Devolve false em qualquer problema — 404, rede fora, servidor antigo sem
   * a rota. A tela só oferece o cadastro quando a resposta é sim, para não
   * mostrar um botão que erra ao ser clicado.
   */
  async cadastroDisponivel(): Promise<boolean> {
    try {
      const r = await fetch(`${BASE}/cadastro`, { method: "GET" });
      return r.ok;
    } catch { return false; }
  },

  /**
   * Cria oficina e proprietário. Exige o código de convite.
   *
   * Não devolve sessão de propósito: o login acontece pelo caminho normal
   * logo depois, que é o único que passa por bloqueio de tentativa e MFA.
   */
  criarOficina: (d: {
    codigo: string; razaoSocial: string; cnpj: string; municipio: string;
    uf: string; ibge: string; nome: string; email: string; senha: string;
  }) => api<{ ok: true; email: string }>("/cadastro", { metodo: "POST", corpo: d }),
};

/* ── domínio ──────────────────────────────────────────────────────────── */

const q = (p: Record<string, any>) => {
  const s = new URLSearchParams(
    Object.entries(p).filter(([, v]) => v != null && v !== "") as any).toString();
  return s ? `?${s}` : "";
};

/**
 * Quantos registros pedir quando a tela quer o cadastro inteiro.
 *
 * Sem `limite`, o servidor devolve só os 100 mais recentes. As telas usam
 * estas listas como tabela de consulta — "de quem é esta OS, qual a placa" —
 * então registro que fica de fora é registro que não existe. Em 16/10 a
 * oficina passou de 100 veículos e o painel foi para branco no login.
 *
 * Com busca o teto padrão basta: é para a pessoa escolher, não para consultar.
 */
const CADASTRO_INTEIRO = 5000;
const listagem = (busca?: string) =>
  q(busca ? { q: busca } : { limite: CADASTRO_INTEIRO });

export const clientes = {
  listar: (busca?: string) => get(`/clientes${listagem(busca)}`),
  ver: (id: string) => get(`/clientes/${id}`),
  criar: (d: any) => post("/clientes", d),
  // A versão vai junto: se o balcão mexeu no mesmo cadastro enquanto o
  // tablet estava sem sinal, o servidor recusa em vez de sobrescrever.
  atualizar: (id: string, d: any) => patch(`/clientes/${id}`, d),
};

export const veiculos = {
  listar: (busca?: string) => get(`/veiculos${listagem(busca)}`),
  criar: (d: any) => post("/veiculos", d),
  // A versão vai junto, como no cliente: placa corrigida no balcão não pode
  // ser desfeita pelo tablet que ficou com a tela aberta desde ontem.
  atualizar: (id: string, d: any) => patch(`/veiculos/${id}`, d),
  garantia: (id: string) => get(`/garantia/${id}`),
};

export const pecas = {
  listar: (busca?: string) => get(`/pecas${listagem(busca)}`),
  criar: (d: any) => post("/pecas", d),
  atualizar: (id: string, d: any) => patch(`/pecas/${id}`, d),
  // Exclusão lógica no servidor (ativo = false): a peça sai da lista mas
  // continua existindo para as OS antigas que a usaram.
  excluir: (id: string) => api(`/pecas/${id}`, { metodo: "DELETE" }),
  criticas: () => get("/estoque/critico"),
  movimentos: () => get("/estoque/movimentos"),
  entrada: (d: any) => post("/estoque/entrada", d),
  inventario: (pecaId: string, contagem: number, observacao: string) =>
    post("/estoque/inventario", { pecaId, contagem, observacao }),
};

export const notas = {
  listar: () => get("/notas"),
};

export const fornecedores = {
  listar: () => get(`/fornecedores${listagem()}`),
  criar: (d: any) => post("/fornecedores", d),
  atualizar: (id: string, d: any) => patch(`/fornecedores/${id}`, d),
  excluir: (id: string) => api(`/fornecedores/${id}`, { metodo: "DELETE" }),
};

export const pedidos = {
  listar: () => get("/pedidos"),
  ver: (id: string) => get(`/pedidos/${id}`),
  criar: (d: { fornecedorId?: string | null; observacao?: string;
                itens: Array<{ pecaId: string; quantidade: number; custoUnitario: number }> }) =>
    post("/pedidos", d),
  status: (id: string, status: string) => patch(`/pedidos/${id}`, { status }),
  receber: (id: string) => post(`/pedidos/${id}/receber`, {}),
};

export const categorias = {
  listar: () => get("/categorias"),
  criar: (nome: string) => post("/categorias", { nome }),
};

export const servicos = {
  listar: (busca?: string) => get(`/servicos${listagem(busca)}`),
  criar: (d: any) => post("/servicos", d),
};

export const os = {
  listar: (f: { status?: string; veiculo?: string; cliente?: string } = {}) => get(`/os${q(f)}`),
  ver: (id: string) => get(`/os/${id}`),
  abrir: (d: any) => post("/os", d),
  addItem: (id: string, item: any) => post(`/os/${id}/itens`, item),
  atualizarItem: (osId: string, itemId: string, d: any) =>
    patch(`/os/${osId}/itens/${itemId}`, d),
  removerItem: (osId: string, itemId: string) =>
    api(`/os/${osId}/itens/${itemId}`, { metodo: "DELETE" }),
  // Aprovar move estoque e cria título. É a operação que mais precisa de
  // chave: aprovar duas vezes geraria dois boletos para o cliente.
  aprovar: (id: string, d: any, idem: string) => post(`/os/${id}/aprovar`, d, idem),
  executar: (id: string) => post(`/os/${id}/executar`),
  finalizar: (id: string, diagnostico?: string, pagamento?: any) =>
    post(`/os/${id}/finalizar`, { diagnostico, pagamento }),
  entregar: (id: string) => post(`/os/${id}/entregar`),
  recusar: (id: string, motivo: string) => post(`/os/${id}/recusar`, { motivo }),
  cancelar: (id: string, motivo: string) => post(`/os/${id}/cancelar`, { motivo }),
  enviar: (id: string) => post(`/os/${id}/enviar`),
  reabrir: (id: string) => post(`/os/${id}/reabrir`),
  excluir: (id: string) => api(`/os/${id}`, { metodo: "DELETE" }),
};

export const orcamentos = {
  listar: () => get("/orcamentos"),
  criar: (d: any) => post("/orcamentos", d),
  aprovar: (id: string, placa: string, idem: string) =>
    post(`/orcamentos/${id}/aprovar`, { placa }, idem),
  recusar: (id: string, motivo: string) => post(`/orcamentos/${id}/recusar`, { motivo }),
  addItem: (id: string, item: any) => post(`/orcamentos/${id}/itens`, item),
  atualizarItem: (id: string, itemId: string, campos: any) =>
    patch(`/orcamentos/${id}/itens/${itemId}`, campos),
  removerItem: (id: string, itemId: string) =>
    api(`/orcamentos/${id}/itens/${itemId}`, { metodo: "DELETE" }),
};

export const financeiro = {
  listar: (f: { tipo?: string; situacao?: string } = {}) => get(`/financeiro${q(f)}`),
  criar: (d: any) => post("/financeiro", d),
  pagar: (id: string, d: any, idem: string) => post(`/financeiro/${id}/pagamento`, d, idem),
};

export const estoqueInteligente = {
  panorama: () => get("/estoque/panorama"),
  margens: () => get("/estoque/margens"),
  reajustar: (itens: Array<{ pecaId: string }>) => post("/estoque/margens/reajustar", { itens }),
  minimos: () => get("/estoque/minimos"),
  aplicarMinimos: (pecaIds: string[], automatizar = false) =>
    post("/estoque/minimos/aplicar", { pecaIds, automatizar }),
  parado: () => get("/estoque/parado"),
  abc: () => get("/estoque/abc"),
};

/**
 * Dados cadastrais da oficina.
 *
 * `salvar` manda só os campos alterados: a função no banco mantém o valor
 * atual para chave ausente, então campo esquecido não vira NULL no cadastro
 * fiscal. CNPJ, código IBGE e plano são recusados lá — não adianta mandar.
 */
/**
 * Equipe da oficina.
 *
 * `convidar` aceita senha opcional. Não há envio de e-mail no sistema, então
 * cadastrar sem senha deixa a pessoa na lista sem conseguir entrar — o campo
 * `temSenha` da listagem é o que mostra quem ainda está pendente.
 */
export const equipe = {
  listar: () => get("/equipe"),
  convidar: (d: { nome: string; email: string; papel: string; senha?: string }) =>
    post("/equipe", d),
  alterar: (usuarioId: string, d: { papel?: string; ativo?: boolean; senha?: string }) =>
    patch(`/equipe/${usuarioId}`, d),
};

export const oficina = {
  ver: () => get("/oficina"),
  salvar: (d: Record<string, any>) => patch("/oficina", d),
};

export const fiscalApi = {
  conexao: () => get("/fiscal/conexao"),
  diagnostico: () => get("/fiscal/diagnostico"),
  pendentes: () => get("/fiscal/pendentes"),
  travados: () => get("/fiscal/travados"),
  emitir: (osId: string, tipo: "nfse" | "nfe", idem: string) =>
    post("/fiscal/emitir", { osId, tipo }, idem),

  /* Ligar a oficina ao fisco. As rotas existiam no servidor desde sempre e
     nenhuma tela chamava — dava para ver o diagnóstico dizendo "pendente" e
     não havia onde resolver. */
  cadastrarEmpresa: (d: any) => post("/fiscal/conexao/empresa", d),
  // O .pfx vai em base64 e NUNCA é gravado: atravessa o servidor e segue
  // para o provedor. Quem tem o arquivo e a senha emite em nome da oficina.
  enviarCertificado: (certificado: string, senha: string) =>
    post("/fiscal/conexao/certificado", { certificado, senha }),
  registrarRetorno: () => post("/fiscal/conexao/webhook", {}),
  ativarProducao: () => post("/fiscal/conexao/producao", {}),
};

export const checklists = {
  listar: () => get("/checklists"),
  ver: (id: string) => get(`/checklists/${id}`),
  criar: (d: any, idem: string) => post("/checklists", d, idem),
};

export const importacao = {
  analisar: (conteudoBase64: string, nome: string) =>
    post("/importacao/analisar", { conteudo: conteudoBase64, nome }),
  estagiar: (conteudoBase64: string, nome: string, mapeamento: any[], margemAlvo?: number) =>
    post("/importacao", { conteudo: conteudoBase64, nome, mapeamento, margemAlvo }),
  conferir: (id: string) => post(`/importacao/${id}/conferir`),
  linhas: (id: string) => get(`/importacao/${id}/linhas`),
  aplicar: (id: string, idem: string) => post(`/importacao/${id}/aplicar`, undefined, idem),
  reverter: (id: string, motivo: string) => post(`/importacao/${id}/reverter`, { motivo }),
};

export const painel = { carregar: () => get("/painel") };

/* ── WhatsApp ─────────────────────────────────────────────────────────── */

/**
 * Link direto, sem API oficial.
 *
 * A API da Meta leva semanas de aprovação. Isto abre o WhatsApp com a
 * mensagem inteira já escrita — zero aprovação, funciona hoje, e entrega
 * quase todo o valor. Trocar depois é mudar esta função.
 */
export function linkWhatsApp(telefone: string, mensagem: string): string {
  const numero = telefone.replace(/\D/g, "");
  const completo = numero.length <= 11 ? `55${numero}` : numero;
  return `https://wa.me/${completo}?text=${encodeURIComponent(mensagem)}`;
}

/* ── fotos ────────────────────────────────────────────────────────────── */

/**
 * Comprime antes de enviar.
 *
 * Foto de celular tem 4 MB. Um checklist com oito delas são 32 MB na rede do
 * galpão — o envio falha, o mecânico tenta de novo, e ele conclui que o
 * sistema não funciona. Redimensionar para 1600px resolve sem perder o que
 * importa: a avaria continua visível e a placa continua legível.
 */
export async function comprimirFoto(arquivo: File, maxLado = 1600, qualidade = 0.8): Promise<Blob> {
  const img = await createImageBitmap(arquivo);
  const escala = Math.min(1, maxLado / Math.max(img.width, img.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * escala);
  canvas.height = Math.round(img.height * escala);
  canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
  return new Promise((r) => canvas.toBlob((b) => r(b!), "image/jpeg", qualidade));
}

/* ── fila offline ─────────────────────────────────────────────────────── */

/**
 * O mecânico está no fundo do galpão sem sinal.
 *
 * Se a OS não abre, o app está morto — e ele volta para o papel no mesmo dia.
 * As escritas entram numa fila local e sobem quando a rede voltar.
 *
 * Simplificação assumida: fila em `localStorage`, que é síncrona e limitada a
 * poucos megabytes. Serve para texto, não para foto. A versão completa usa
 * IndexedDB e o protocolo de `sync.ts`, que já existe no backend com detecção
 * de conflito. Isto é o suficiente para o piloto, não para escala.
 */
const CHAVE_FILA = "bs.fila";

type Pendente = { id: string; caminho: string; metodo: string; corpo: any; em: string };

const lerFila = (): Pendente[] => JSON.parse(localStorage.getItem(CHAVE_FILA) ?? "[]");
const gravarFila = (f: Pendente[]) => localStorage.setItem(CHAVE_FILA, JSON.stringify(f));

export function enfileirar(caminho: string, metodo: string, corpo: any): string {
  const id = crypto.randomUUID();
  gravarFila([...lerFila(), { id, caminho, metodo, corpo, em: new Date().toISOString() }]);
  return id;
}

export async function sincronizarFila(): Promise<{ enviadas: number; falharam: number }> {
  const fila = lerFila();
  if (!fila.length) return { enviadas: 0, falharam: 0 };

  const restantes: Pendente[] = [];
  let enviadas = 0;

  for (const p of fila) {
    try {
      // O id do item é a chave de idempotência: se a conexão caiu depois do
      // servidor processar, o reenvio não duplica.
      await api(p.caminho, { metodo: p.metodo, corpo: p.corpo, idempotencia: p.id });
      enviadas++;
    } catch (e) {
      // 4xx é dado errado: insistir não resolve e trava a fila inteira atrás
      // de um item que nunca vai passar. Descarta e registra.
      if (e instanceof ErroApi && e.status >= 400 && e.status < 500 && e.status !== 429) {
        console.error("[fila] descartado", p.caminho, e.message);
      } else {
        restantes.push(p);
      }
    }
  }
  gravarFila(restantes);
  return { enviadas, falharam: restantes.length };
}

export const temPendencias = () => lerFila().length;

export function observarConexao(aoVoltar: (r: { enviadas: number }) => void) {
  const h = async () => { const r = await sincronizarFila(); if (r.enviadas) aoVoltar(r); };
  window.addEventListener("online", h);
  const t = setInterval(() => { if (navigator.onLine) h(); }, 30_000);
  return () => { window.removeEventListener("online", h); clearInterval(t); };
}
